import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Users, Settings, Check, X, Save } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';

const TEMPLATES = [
  { id: 'business', name: 'Business Card', icon: '💼' },
  { id: 'landing', name: 'Landing Page', icon: '🚀' },
  { id: 'portfolio', name: 'Portfolio', icon: '🎨' },
  { id: 'luxury', name: 'Luxury', icon: '👑' },
  { id: 'minimal', name: 'Minimal', icon: '✨' }
];

const DEFAULT_PERMISSIONS = {
  max_pages: 1,
  allowed_templates: ['business'],
  can_use_gallery: false,
  can_use_video: false,
  can_use_contact_form: false,
  can_use_custom_colors: false,
  can_remove_branding: false,
  gallery_image_limit: 5,
  text_section_limit: 3,
  cta_button_limit: 3,
  social_link_limit: 5
};

const UserPagePermissions = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [permissions, setPermissions] = useState(DEFAULT_PERMISSIONS);
  const [isEnabled, setIsEnabled] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/admin/users');
      // Show all users
      setUsers(res.data.users || []);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadUserSettings = async (user) => {
    setSelectedUser(user);
    try {
      const res = await api.get('/admin/pages/user-settings');
      const userSettings = res.data.find(s => s.user_id === user.id);
      if (userSettings) {
        setPermissions(userSettings.permissions || DEFAULT_PERMISSIONS);
        setIsEnabled(userSettings.is_enabled);
      } else {
        setPermissions(DEFAULT_PERMISSIONS);
        setIsEnabled(false);
      }
    } catch (error) {
      setPermissions(DEFAULT_PERMISSIONS);
      setIsEnabled(false);
    }
  };

  const savePermissions = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await api.post(`/admin/pages/user-settings/${selectedUser.id}?is_enabled=${isEnabled}`, permissions);
      toast.success(`Page builder ${isEnabled ? 'enabled' : 'disabled'} for ${selectedUser.name}`);
    } catch (error) {
      toast.error('Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const toggleTemplate = (templateId) => {
    const current = permissions.allowed_templates || [];
    if (current.includes(templateId)) {
      setPermissions({
        ...permissions,
        allowed_templates: current.filter(t => t !== templateId)
      });
    } else {
      setPermissions({
        ...permissions,
        allowed_templates: [...current, templateId]
      });
    }
  };

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-page-permissions">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/admin/page-builder')} className="p-2 hover:bg-white/10 rounded-lg">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="w-6 h-6 text-primary" />
            User Page Permissions
          </h1>
          <p className="text-muted-foreground">Control which users can create pages and their limits</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User List */}
        <div className="glass-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <Search className="w-5 h-5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search activated users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
            />
          </div>

          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {filteredUsers.map(user => (
              <button
                key={user.id}
                onClick={() => loadUserSettings(user)}
                className={`w-full p-4 rounded-xl text-left transition-all ${
                  selectedUser?.id === user.id 
                    ? 'bg-primary/20 border-2 border-primary' 
                    : 'bg-white/5 hover:bg-white/10 border-2 border-transparent'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{user.name}</p>
                    <p className="text-sm text-muted-foreground">@{user.username}</p>
                  </div>
                  {user.is_activated && (
                    <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs">
                      Activated
                    </span>
                  )}
                </div>
              </button>
            ))}
            {filteredUsers.length === 0 && (
              <p className="text-center text-muted-foreground py-8">No activated users found</p>
            )}
          </div>
        </div>

        {/* Permissions Editor */}
        <div className="glass-card p-6">
          {selectedUser ? (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold">{selectedUser.name}</h3>
                  <p className="text-sm text-muted-foreground">@{selectedUser.username}</p>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className={isEnabled ? 'text-green-400' : 'text-muted-foreground'}>
                    {isEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                  <button
                    onClick={() => setIsEnabled(!isEnabled)}
                    className={`w-12 h-6 rounded-full transition-all ${
                      isEnabled ? 'bg-green-500' : 'bg-gray-600'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
                      isEnabled ? 'translate-x-6' : 'translate-x-0.5'
                    }`} />
                  </button>
                </label>
              </div>

              {isEnabled && (
                <>
                  {/* Max Pages */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Maximum Pages</label>
                    <input
                      type="number"
                      min={1}
                      max={100}
                      value={permissions.max_pages}
                      onChange={(e) => setPermissions({ ...permissions, max_pages: parseInt(e.target.value) || 1 })}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                    />
                  </div>

                  {/* Allowed Templates */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Allowed Templates</label>
                    <div className="grid grid-cols-2 gap-2">
                      {TEMPLATES.map(template => (
                        <button
                          key={template.id}
                          onClick={() => toggleTemplate(template.id)}
                          className={`p-3 rounded-xl flex items-center gap-2 transition-all ${
                            (permissions.allowed_templates || []).includes(template.id)
                              ? 'bg-primary/20 border-2 border-primary'
                              : 'bg-white/5 border-2 border-transparent hover:bg-white/10'
                          }`}
                        >
                          <span>{template.icon}</span>
                          <span className="text-sm">{template.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Feature Toggles */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Features</label>
                    <div className="space-y-2">
                      {[
                        { key: 'can_use_gallery', label: 'Photo Gallery' },
                        { key: 'can_use_video', label: 'Video Embed' },
                        { key: 'can_use_contact_form', label: 'Contact Form' },
                        { key: 'can_use_custom_colors', label: 'Custom Colors' },
                        { key: 'can_remove_branding', label: 'Remove Branding' }
                      ].map(feature => (
                        <label key={feature.key} className="flex items-center justify-between p-3 bg-white/5 rounded-xl cursor-pointer hover:bg-white/10">
                          <span>{feature.label}</span>
                          <div className={`w-10 h-5 rounded-full transition-all ${
                            permissions[feature.key] ? 'bg-green-500' : 'bg-gray-600'
                          }`}>
                            <div 
                              className={`w-4 h-4 rounded-full bg-white transition-transform mt-0.5 ${
                                permissions[feature.key] ? 'translate-x-5' : 'translate-x-0.5'
                              }`} 
                              onClick={() => setPermissions({ ...permissions, [feature.key]: !permissions[feature.key] })}
                            />
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Limits */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs mb-1">Gallery Images</label>
                      <input
                        type="number"
                        min={1}
                        max={50}
                        value={permissions.gallery_image_limit}
                        onChange={(e) => setPermissions({ ...permissions, gallery_image_limit: parseInt(e.target.value) || 5 })}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs mb-1">Text Sections</label>
                      <input
                        type="number"
                        min={1}
                        max={20}
                        value={permissions.text_section_limit}
                        onChange={(e) => setPermissions({ ...permissions, text_section_limit: parseInt(e.target.value) || 3 })}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs mb-1">CTA Buttons</label>
                      <input
                        type="number"
                        min={1}
                        max={10}
                        value={permissions.cta_button_limit}
                        onChange={(e) => setPermissions({ ...permissions, cta_button_limit: parseInt(e.target.value) || 3 })}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs mb-1">Social Links</label>
                      <input
                        type="number"
                        min={1}
                        max={20}
                        value={permissions.social_link_limit}
                        onChange={(e) => setPermissions({ ...permissions, social_link_limit: parseInt(e.target.value) || 5 })}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                </>
              )}

              <button
                onClick={savePermissions}
                disabled={saving}
                className="w-full py-3 bg-primary text-black font-semibold rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Save className="w-5 h-5" />
                {saving ? 'Saving...' : 'Save Permissions'}
              </button>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <Settings className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Select a user to manage their page builder permissions</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserPagePermissions;
